import { useMemo, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { FileText, Folder, Upload, Video } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { elements as allElements } from "@/data/index";
import {
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExpedienteDocs,
  useExpedienteForCandidateStandard,
} from "@/store/hooks";

function useQuery() {
  const [loc] = useLocation();
  return useMemo(() => {
    const s = typeof window !== "undefined" ? window.location.search : "";
    return new URLSearchParams(s);
  }, [loc, typeof window !== "undefined" ? window.location.search : ""]);
}

export default function CandidateEvidencias() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const fallbackId = candList[0]?.id;
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : fallbackId;

  const enrollments = useEnrollmentsForCandidate(candidateId);
  const q = useQuery();
  const requested = q.get("codigo");
  const codigo = requested && enrollments.includes(requested) ? requested : enrollments[0];

  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const docs = useExpedienteDocs(exp?.id);
  const updateDoc = useDemoStore((s) => s.updateExpedienteDoc);
  const logEvent = useDemoStore((s) => s.logEvent);

  const { criterioMap, staticEvidencias } = useMemo(() => {
    const criterioMap: Record<string, { codigo: string; tipo: string }> = {};
    const staticEvidencias: Array<{ id: string; elementCodigo: string; nombre: string; tipo: string; obligatoria: boolean; criterioIds: string[] }> = [];
    for (const el of allElements) {
      for (const c of el.criterios) criterioMap[c.id] = { codigo: c.codigo, tipo: c.tipo };
      for (const ev of el.evidencias) {
        staticEvidencias.push({ id: ev.id, elementCodigo: el.codigo, nombre: ev.nombre, tipo: ev.tipo, obligatoria: ev.obligatoria, criterioIds: ev.criterioIds ?? [] });
      }
    }
    return { criterioMap, staticEvidencias };
  }, []);

  const onUpload = (docId: string) => {
    if (!exp) return;
    updateDoc(exp.id, docId, { estado: "cargado", subidoEn: new Date().toISOString().slice(0, 10) });
    logEvent({ tipo: "evidencia_cargada", descripcion: `Evidencia ${docId} cargada por el candidato`, actor: candidateId ?? "candidato", expedienteId: exp.id });
  };

  if (!candidateId || enrollments.length === 0) {
    return (
      <AppLayout breadcrumb={["Mis evidencias"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>No está inscrito en ningún estándar.</div></Card>
      </AppLayout>
    );
  }

  const cargados = docs.filter((d) => d.estado === "cargado").length;
  const observados = docs.filter((d) => d.estado === "observado").length;
  const total = docs.length;

  return (
    <AppLayout breadcrumb={["Mis evidencias"]}>
      {enrollments.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {enrollments.map((c) => (
            <Link key={c} href={`/alumno/evidencias?codigo=${c}`}>
              <button style={{
                padding: "6px 14px", borderRadius: 8, border: "1px solid var(--cc-gray-200)",
                background: c === codigo ? "var(--cc-teal-bg)" : "white",
                color: c === codigo ? "var(--cc-teal)" : "var(--cc-gray-700)",
                cursor: "pointer", fontSize: 13, fontWeight: 600,
              }}>{c}</button>
            </Link>
          ))}
        </div>
      )}

      <Card>
        <SectionTitle>Documentos y evidencias — {codigo}</SectionTitle>
        <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginBottom: 12 }}>
          {cargados} de {total} cargadas · {observados} con observaciones
        </div>

        {docs.length === 0 ? (
          <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Este estándar aún no tiene evidencias configuradas.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--cc-gray-100)" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "40%" }}>Evidencia</th>
                <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "30%" }}>Criterios</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "15%" }}>Estado</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "15%" }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d, docIdx) => {
                const matched = staticEvidencias[docIdx];
                const displayNombre = matched?.nombre ?? d.nombre;
                const displayTipo = matched?.tipo ?? d.tipo;
                const displayObligatoria = matched?.obligatoria ?? d.obligatoria;
                const icon = displayTipo === "video" ? Video : displayTipo === "foto" ? Folder : FileText;
                const Icon = icon;
                const tone = d.estado === "cargado" ? "green" : d.estado === "observado" ? "amber" : "gray";
                const docCriterioIds = matched?.criterioIds?.length ? matched.criterioIds : (d.criterioIds ?? []);
                return (
                  <tr key={d.id} data-testid={`doc-${d.id}`} style={{ borderBottom: "1px solid var(--cc-gray-100)" }}>
                    <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: "var(--cc-gray-100)", color: "var(--cc-gray-600)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon size={14} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: "var(--cc-navy)" }}>
                            {displayNombre} {displayObligatoria && <span style={{ color: "var(--cc-red)", fontSize: 10 }}>*</span>}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                            {displayTipo} · {d.estado === "cargado" ? `Cargada${d.subidoEn ? ` el ${d.subidoEn}` : ""}` : d.estado === "observado" ? (d.observacion ?? "Con observaciones") : "Faltante"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                        {matched?.elementCodigo && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-400)", whiteSpace: "nowrap", paddingTop: 3, flexShrink: 0 }}>
                            {matched.elementCodigo}
                          </span>
                        )}
                        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          {docCriterioIds.map((cid) => {
                            const c = criterioMap[cid];
                            if (!c) return null;
                            const chipColor =
                              c.tipo === "producto" ? "#7c3aed" :
                              c.tipo === "actitud" ? "#d97706" :
                              c.tipo === "conocimiento" ? "#16a34a" :
                              "var(--cc-teal)";
                            return (
                              <span key={cid} title={c.tipo} style={{
                                fontSize: 10, fontWeight: 700, padding: "2px 5px",
                                borderRadius: 4, background: `${chipColor}15`,
                                color: chipColor, border: `1px solid ${chipColor}30`,
                                letterSpacing: "0.02em", whiteSpace: "nowrap",
                              }}>
                                {c.codigo}
                              </span>
                            );
                          })}
                          {docCriterioIds.length === 0 && <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>—</span>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", verticalAlign: "middle" }}>
                      <Badge tone={tone as "green" | "amber" | "gray"}>{d.estado.toUpperCase()}</Badge>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", verticalAlign: "middle" }}>
                      {d.estado !== "cargado" && (
                        <PrimaryButton variant="ghost" onClick={() => onUpload(d.id)} testId={`button-upload-${d.id}`}>
                          <Upload size={12} /> Subir
                        </PrimaryButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </AppLayout>
  );
}
